import { RootState, store } from '@/app/store';
import { selectAllPortfolioBalances, useGetPortfolioBalancesQuery } from '@/features/balance/balancesApi';
import AssetBalanceDetails from '@/shared/components/AssetBalanceDetails/AssetBalanceDetails';
import DepositWithdraw from '@/shared/components/DepositWithdraw/DepositWithdraw';
import { calculateEuroValue } from '@/shared/helpers/portfolioHelpers';
import { IonCol, IonSearchbar } from '@ionic/react';
import { RefObject, useCallback, useMemo } from 'react';
import { selectIsLoggedIn } from '@/features/oAuth/oAuthSlice';
import { useMarketInfo } from '@/shared/hooks/market';
import { useSelector } from 'react-redux';
import styles from './Wallet.module.css';
import { useSelectedAssetId } from '@/shared/hooks/assets';
import { useIsMobile } from '@/shared/hooks/devices';
import { useGetCryptoWithdrawalsQuery } from '@/features/crypto/cryptoApi';
import { WrappedWithdrawalFlow, WrappedDepositFlow } from '@blocknox/flow';
import { useTranslation } from 'react-i18next';
import PendingDeposits from '@/shared/components/PendingDeposits/PendingDeposits';
import { selectCryptoDepositsByAssetIdAndState } from '@/features/crypto/cryptoApi';
import { depositCryptoState, TravelRuleType, withdrawCryptoState } from '@/types/crypto';
import '@/../node_modules/@blocknox/flow/dist/style.css';

type Props = {
  initiateTravelRule: boolean;
  initiateDepositTravelRule: boolean;
  withdrawCryptoData?: withdrawCryptoState;
  depositCryptoData?: depositCryptoState;
  withdrawCryptoChallengeData?: any;
  isOpen: boolean;
  modal: RefObject<HTMLIonModalElement>;
  setIsOpen: (value: boolean) => void;
  handleInitTravelRule: (value: boolean, withdrawCryptoData: withdrawCryptoState, type?: TravelRuleType) => void;
  handleChallenge: () => void;
  handleSuccessfulDeposits: () => void;
};

const WalletDetail: React.FC<Props> = ({
  initiateTravelRule,
  initiateDepositTravelRule,
  withdrawCryptoData,
  depositCryptoData,
  withdrawCryptoChallengeData,
  isOpen,
  modal,
  setIsOpen,
  handleInitTravelRule,
  handleChallenge,
  handleSuccessfulDeposits
}) => {
  const isMobile = useIsMobile();
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const { marketsInfo } = useMarketInfo();
  const { i18n } = useTranslation();

  const selectedAssetId = useSelectedAssetId();

  const { data: portfolioBalancesData } = useGetPortfolioBalancesQuery(undefined, {
    skip: !isLoggedIn
  });

  const { data: withdrawals } = useGetCryptoWithdrawalsQuery(
    { assetId: selectedAssetId?.toString() || '', sortOrder: 'desc' },
    {
      skip: !isLoggedIn || !selectedAssetId
    }
  );

  const assetsPortfolioBalances = selectAllPortfolioBalances(store.getState());

  const walletBalanceEuro = useMemo(
    () => calculateEuroValue(assetsPortfolioBalances, marketsInfo),
    [assetsPortfolioBalances, marketsInfo]
  );

  const assetBalance = walletBalanceEuro.find((balance) => balance.assetId === selectedAssetId);

  const lockedByWithdrawals = useMemo(
    () =>
      withdrawals
        ?.filter(
          (withdrawal) =>
            ['accepted', 'processing', 'pending'].includes(withdrawal.transactionState) &&
            withdrawal.assetId === selectedAssetId
        )
        .reduce((prev, curr) => prev + Number(curr.amount), 0),
    [selectedAssetId, withdrawals]
  );

  assetBalance && (assetBalance.locked = lockedByWithdrawals);

  const onDepositsCancel = useCallback(() => {
    handleInitTravelRule(
      false,
      {
        id: null,
        intentId: null
      },
      TravelRuleType.Deposit
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedStringAssetId = selectedAssetId?.toString() || '';

  const allTRCryptoDeposits = useSelector((state: RootState) =>
    selectCryptoDepositsByAssetIdAndState(state, selectedStringAssetId, 'requirestravelruleinformation')
  );

  const isDepositsTravelRule = initiateDepositTravelRule && depositCryptoData?.intentId !== null;
  const isWithdrawalsTravelRule = initiateTravelRule && withdrawCryptoData?.intentId !== null;

  return (
    <IonCol sizeLg="7" sizeMd="12" className={`${styles.backgroundShown}`}>
      {(isDepositsTravelRule && depositCryptoData) || (isWithdrawalsTravelRule && withdrawCryptoData) ? (
        <>
          {isWithdrawalsTravelRule && withdrawCryptoData?.intentId ? (
            <WrappedWithdrawalFlow
              intentId={withdrawCryptoData?.intentId}
              onCancel={() => {
                handleInitTravelRule(false, {
                  id: null,
                  intentId: null
                });
              }}
              onSuccess={handleChallenge}
            />
          ) : isDepositsTravelRule && depositCryptoData?.intentId ? (
            <WrappedDepositFlow
              transactionId={depositCryptoData.intentId}
              onCancel={onDepositsCancel}
              onSuccess={handleSuccessfulDeposits}
            />
          ) : null}
        </>
      ) : (
        <div>
          {portfolioBalancesData && (
            <div>
              <AssetBalanceDetails assetBalance={assetBalance} />
            </div>
          )}
          <div className={isMobile ? styles.wrapperMobile : styles.wrapper}>
            <IonSearchbar style={{ display: 'none' }} />
            <DepositWithdraw
              handleInitTravelRule={handleInitTravelRule}
              withdrawCryptoChallengeData={withdrawCryptoChallengeData}
              modal={modal}
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              key={selectedAssetId}
              assetBalance={assetBalance}
            />
            {selectedAssetId?.toString() !== 'eur' && allTRCryptoDeposits.length > 0 && (
              <div>
                <PendingDeposits
                  allTRCryptoDeposits={allTRCryptoDeposits}
                  handleInitiateTravelRule={handleInitTravelRule}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </IonCol>
  );
};

export default WalletDetail;
